/**
 * Authentication context for Attorneys (Attorney Portal).
 *
 * Clerk-only cutover (2026-07-22): attorneys authenticate via their Clerk
 * session (an active Organization), never a bespoke attorney JWT. The
 * legacy `attorney_token`/`attorney_profile` localStorage machinery and the
 * `/api/v1/attorney/login` endpoint it POSTed to have been fully retired on
 * the backend, which now rejects legacy attorney JWTs outright — this
 * frontend context no longer reads, writes, or mints them.
 *
 * `token`/`attorney`/`logout`/`getAuthHeader` remain on the context shape
 * because several attorney pages/hooks still destructure them, but they are
 * now inert (no legacy session ever existed to hydrate): `token`/`attorney`
 * stay null, `logout`/`getAuthHeader` are no-ops. Pages that need an auth
 * header should call `getAuthHeaders()` (see `lib/authHeaders.ts`), which
 * now falls back straight to the Clerk session token.
 */

import React, { createContext, useContext } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import { isSeatRoleSotEnabled } from '../config/seatRoles';

export interface AttorneyProfile {
  id: string;
  email: string;
  name: string;
  law_firm_id: string;
  law_firm_name: string;
  phone?: string;
  last_login?: string;
}

interface AttorneyAuthContextType {
  attorney: AttorneyProfile | null;
  token: string | null;
  loading: boolean;
  logout: () => void;
  getAuthHeader: () => { Authorization: string } | {};
}

const AttorneyAuthContext = createContext<AttorneyAuthContextType | undefined>(undefined);

export function AttorneyAuthProvider({ children }: { children: React.ReactNode }) {
  // No local session to hydrate — attorneys authenticate via Clerk only.
  const logout = () => {
    // No-op: there is no local attorney session left to clear. Signing out
    // of Clerk is handled by the app's shared Clerk sign-out flow.
  };

  const getAuthHeader = () => ({});

  return (
    <AttorneyAuthContext.Provider
      value={{
        attorney: null,
        token: null,
        loading: false,
        logout,
        getAuthHeader,
      }}
    >
      {children}
    </AttorneyAuthContext.Provider>
  );
}

export function useAttorneyAuth() {
  const context = useContext(AttorneyAuthContext);
  if (context === undefined) {
    throw new Error('useAttorneyAuth must be used within AttorneyAuthProvider');
  }
  return context;
}

/**
 * Route guard for the attorney portal.
 *
 * Clerk-only (post-cutover, 2026-07-22): admits ONLY a Clerk-signed-in
 * session with an active Organization (`orgId`) — the real authorization
 * gate is the backend's `get_current_attorney_actor` (org ->
 * law_firms.clerk_org_id -> attorney); this is routing only.
 *
 * A Clerk user signed in with NO active org is staff, not an attorney —
 * bounced to the main dashboard rather than the attorney login loop.
 */
/**
 * Company-staff session detector (2026-07-23, production-house cross-firm model).
 * ClaimGenius staff (owner/admin/accountant/data_specialist) produce + verify +
 * attest every firm's cases, so they may enter the attorney UI without a firm
 * org — matching the backend `get_current_attorney_actor` platform-staff branch.
 * Mirrors the backend `flyght30@gmail.com → owner` email hardcode so the owner
 * is admitted regardless of Clerk metadata. Excludes the external witness CPA
 * (accountant_external / cpa), attorneys, and clients — they are NOT company staff.
 */
export function isCompanyStaffSession(
  user: ReturnType<typeof useUser>['user'] | null | undefined,
): boolean {
  if (!user) return false;
  const email = (user.primaryEmailAddress?.emailAddress || '').toLowerCase();
  // Owner-email allowlist from build env (REACT_APP_OWNER_EMAILS, comma-separated)
  // — no personal email in source. If unset, staff are admitted purely by their
  // Clerk role below (set your Clerk role to owner/admin, or set the env).
  const ownerEmails = (process.env.REACT_APP_OWNER_EMAILS || '')
    .split(',')
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean);
  if (email && ownerEmails.includes(email)) return true;
  // REACT_APP_SEAT_ROLE_SOT on (Phase 7, D8): publicMetadata only — unsafeMetadata is user-writable.
  const seatRoleSot = isSeatRoleSotEnabled();
  const raw = String(
    (user.publicMetadata?.role as string) || (seatRoleSot ? '' : (user.unsafeMetadata?.role as string)) || '',
  )
    .toLowerCase()
    .replace(/-/g, '_');
  if (!raw) return false;
  if (raw === 'owner' || raw.includes('admin')) return true;
  // On-staff cross-firm attorney reviewer (FE mirror of the backend cross-firm gate).
  if (raw === 'attorney_internal') return true;
  if (raw.includes('data') && raw.includes('special')) return true;
  // Internal accountant only — the external/witness CPA is not company staff.
  if (raw.includes('account') && !raw.includes('external') && raw !== 'cpa') return true;
  return false;
}

export function ProtectedAttorneyRoute({ children }: { children: React.ReactElement }) {
  const { isLoaded: clerkLoaded, isSignedIn, orgId } = useAuth();
  // useUser() hydrates the full User object (email + publicMetadata) on its
  // OWN isLoaded clock, which lags useAuth()'s session/org load. The
  // company-staff admit reads `user`, so we MUST wait for userLoaded too —
  // otherwise there's a window where isSignedIn===true, orgId===undefined,
  // user===null, and the guard bounces staff to /dashboard before their
  // user object (and thus isCompanyStaffSession) is even available.
  const { isLoaded: userLoaded, user } = useUser();

  if (!clerkLoaded || !userLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-bg-base)' }}>
        <svg
          className="animate-spin h-8 w-8"
          style={{ color: 'var(--color-needs-human-text)' }}
          viewBox="0 0 24 24"
          data-testid="attorney-auth-loading"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  // Admit a firm attorney (active Clerk org) OR company staff (cross-firm
  // production-house oversight — owner/admin/accountant/data_specialist).
  if (isSignedIn && (orgId || isCompanyStaffSession(user))) {
    return children;
  }

  if (isSignedIn) {
    // Signed in to Clerk but neither a firm attorney nor company staff —
    // send to the staff dashboard rather than loop the attorney login.
    return <Navigate to="/dashboard" replace />;
  }

  return <Navigate to="/attorney/login" replace />;
}
